import { Calendar, Check, PlayCircle } from "lucide-react";

export const CurriculumSection: React.FC<{ data: any; theme: any }> = ({
  data,
  theme,
}) => {
  if (!data?.modules || data.modules.length === 0) return null;

  return (
    <section id="curriculum" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
            {data.sectionTitle || "Curriculum"}
          </h2>
          {data.description && (
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-6 leading-relaxed">
              {data.description}
            </p>
          )}
          {data.totalDuration && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
              <Calendar size={18} className="text-gray-600" />
              <span className="font-semibold text-gray-700">
                Total Duration: {data.totalDuration}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {[...data.modules]
            .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
            .map((module: any, index: number) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow"
              >
                <div className="p-6 sm:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div
                      className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg"
                      style={{
                        backgroundColor: theme?.primaryColor || "#10B981",
                      }}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                        {module.title}
                      </h3>
                      {module.description && (
                        <p className="text-gray-600 mb-3 leading-relaxed">
                          {module.description}
                        </p>
                      )}
                      {module.duration && (
                        <div className="inline-flex items-center gap-1 text-sm text-gray-500">
                          <PlayCircle size={14} />
                          <span>{module.duration}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {module.lessons && module.lessons.length > 0 && (
                    <div className="space-y-2 pl-0 sm:pl-18">
                      {[...module.lessons]
                        .sort(
                          (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
                        )
                        .map((lesson: any, lessonIndex: number) => (
                          <div
                            key={lessonIndex}
                            className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div
                              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                              style={{
                                backgroundColor: `${theme?.primaryColor || "#10B981"}20`,
                              }}
                            >
                              <Check
                                size={14}
                                style={{
                                  color: theme?.primaryColor || "#10B981",
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900">
                                {lesson.lessonTitle}
                              </p>
                              {lesson.lessonDescription && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {lesson.lessonDescription}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-2">
                                {lesson.lessonDuration && (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <PlayCircle size={12} />
                                    {lesson.lessonDuration}
                                  </span>
                                )}
                                {lesson.lessonType &&
                                  lesson.lessonType !== "video" && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full capitalize">
                                      {lesson.lessonType}
                                    </span>
                                  )}
                                {lesson.isPreview && (
                                  <span
                                    className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                                    style={{
                                      backgroundColor:
                                        theme?.primaryColor || "#10B981",
                                    }}
                                  >
                                    Preview
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};
